import { Inject, Injectable } from '@angular/core';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { Select } from '@ngxs/store';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  AdministrationHttpService,
  AuthHttpService,
  ConfigHttpService,
  DashboardHttpService,
  EventSubHttpService,
  FileHttpService,
  ICListHttpService,
  LocationHttpService,
  SmsHttpService,
  StorageHttpService,
} from '@sc/data-access-http-services';
import {
  Appointment,
  AuthenticationMethod,
  CLIENT_APP_ID,
  ClientAppId,
  Configuration,
  CountryBounds,
  Criteria,
  Document,
  Dossier,
  DossierToken,
  Fitter,
  GeoCoordinates,
  GetSuperAdminSMSStatsAPI,
  Itinerary,
  LocationData,
  LocationDataGeocoded,
  OktaAccount,
  PreDeclarationToken,
  SearchDashboardAPIParams,
  SearchDate,
  SendSMSLinkApi,
  SuperAdminSmsStatisticsAPIParams,
  SuperAdminStatisticsAPIParams,
  SuperAdminStatisticsApiResponse,
  UploadStatus,
  UploadType,
  exchangeDossierTokenApi,
  isUploadStatusRunning,
  SuperAdminCosmosExportApiResponse,
} from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import {
  ExcelFileExporterService,
  SmsStatsSheetRow,
} from '@sc/feature-file-export';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { dayjs } from '@sc/utils';
import { Observable, interval, of } from 'rxjs';
import { first, map, retry, share, switchMap, take, tap } from 'rxjs/operators';
import {
  NavigationAction,
  NavigationStep,
  SetCurrentStepAction,
  UploadPartnerStatusInitAction,
  UploadPartnerStatusUpdateAction,
} from '../store';
import { DossierSandboxService } from './dossier';
import { FittersSandboxService } from './fitters';

@Injectable({ providedIn: 'root' })
export class SandboxService {
  @Select((state) => state.files?.uploadPartners)
  public uploadPartnerStatus$: Observable<UploadStatus>;

  @Select((state) => state.nav.action)
  public navigation$: Observable<NavigationAction>;

  @Select((state) => state.nav.step)
  public currentStep$: Observable<NavigationStep>;

  private itineraryCache: Record<string, Itinerary> = {};

  constructor(
    @Inject(CLIENT_APP_ID) private readonly clientAppId: ClientAppId,
    private readonly configService: ConfigurationService,
    private readonly locationHttpService: LocationHttpService,
    private readonly authHttpService: AuthHttpService,
    private readonly smsHttpService: SmsHttpService,
    private readonly icListttpService: ICListHttpService,
    private readonly eventSubHttpService: EventSubHttpService,
    private readonly dashboardHttpService: DashboardHttpService,
    private readonly administrationHttpService: AdministrationHttpService,
    private readonly storageHttpService: StorageHttpService,
    private readonly fileHttpService: FileHttpService,
    private readonly featureFlippingService: FeatureFlippingManagerService,
    private readonly dossierSB: DossierSandboxService,
    private readonly fitterSB: FittersSandboxService,
    private readonly excelFileExporterService: ExcelFileExporterService,
    private readonly translator: TranslatorService,
    private readonly configHttp: ConfigHttpService
  ) {}

  getGeoCodeFromAddress(address: string): Observable<LocationDataGeocoded> {
    return this.locationHttpService.getGeoCodeFromAddress(address);
  }

  getCountryBounds(): Observable<CountryBounds> {
    return this.locationHttpService.getCountryBounds();
  }

  getPlacesFromAddress(address: string): Observable<LocationData[]> {
    return this.locationHttpService.getPlacesFromAddress(address);
  }

  getPlacesFromPostalCode(postalCode: number) {
    return this.locationHttpService.getPlacesFromPostalCode(postalCode);
  }

  getPostalCodeFromGeoCode(geoCoordinates: GeoCoordinates) {
    return this.locationHttpService.getPostalCodeFromGeoCode(geoCoordinates);
  }

  getGeoCodeFromPlaceId(placeId: string): Observable<LocationDataGeocoded> {
    return this.locationHttpService.getGeoCodeFromPlaceId(placeId);
  }

  getPlaceDataFromGeoCode(
    lat: number,
    lon: number
  ): Observable<LocationDataGeocoded> {
    return this.locationHttpService.getPlaceDataFromGeoCode(lat, lon);
  }

  private formatItineraryCacheKey(
    originPlaceId: string,
    destination: GeoCoordinates
  ) {
    return `${originPlaceId}##${destination.lat}##${destination.lon}`;
  }

  getItinerary(
    originPlaceId: string,
    destination: GeoCoordinates,
    departureDateTimeInMillis: number
  ): Observable<Itinerary> {
    if (!originPlaceId || !destination?.lat || !destination?.lon) {
      return of(null);
    }

    const cacheKey = this.formatItineraryCacheKey(originPlaceId, destination);
    const cachedItinerary = this.itineraryCache[cacheKey] || null;

    if (!cachedItinerary) {
      return this.locationHttpService
        .getGoogleItinerary(
          `place_id:${originPlaceId}`,
          `${destination.lat},${destination.lon}`,
          departureDateTimeInMillis
        )
        .pipe(
          take(1),
          tap((resultItinerary) => {
            this.itineraryCache[cacheKey] = resultItinerary;
          })
        );
    }

    return of(cachedItinerary);
  }

  getDossierItinerary(dossier?: Dossier, fitter?: Fitter) {
    if (dossier?.appointment?.itinerary) {
      return of(dossier?.appointment?.itinerary);
    }

    const usedDossierObs = dossier
      ? of(dossier)
      : this.dossierSB.dossier$.pipe(take(1));

    return usedDossierObs.pipe(
      switchMap((dos) => {
        if (fitter) {
          return of([dos, fitter] as const);
        } else {
          return this.fitterSB.fitters$.pipe(
            take(1),
            map(
              (fitters) =>
                [
                  dos,
                  fitters.find((f) => f.id === dos.appointment?.fitterId),
                ] as const
            )
          );
        }
      }),
      switchMap(([dos, f]) =>
        this.getItinerary(
          dos?.searchCriteria?.address?.placeId,
          f?.location,
          dayjs(dossier.appointment?.time).unix()
        )
      )
    );
  }

  getDossierToken(encryptedInfo: string): Observable<DossierToken> {
    return this.authHttpService.getDossierToken({ encryptedInfo });
  }

  getPreDeclarationToken(token: string): Observable<PreDeclarationToken> {
    return this.authHttpService.getPreDeclarationToken({ token });
  }

  exchangeDossierToken(
    params: exchangeDossierTokenApi
  ): Observable<DossierToken> {
    return this.authHttpService.exchangeDossierToken(params);
  }

  doAuthentication(
    email: string,
    authenticationMethod?: AuthenticationMethod,
    forgotPassword?: boolean
  ): Observable<Object> {
    return this.authHttpService.doAuthenticationInsurer(
      email,
      authenticationMethod,
      forgotPassword
    );
  }

  signIn(email: string, password: string): Observable<Object> {
    return this.authHttpService.signIn(email, password);
  }

  superAdminSignIn(email: string, password: string): Observable<Object> {
    return this.authHttpService.superAdminSignIn(email, password);
  }

  resetPassword(encryptedInfo: string, password: string): Observable<Object> {
    return this.authHttpService.resetPassword(encryptedInfo, password);
  }

  sendLinkToCustomer(smsParams: SendSMSLinkApi) {
    return this.smsHttpService.sendLinkToCustomer(smsParams);
  }

  getClientKey(): string {
    return this.eventSubHttpService.getClientKey();
  }

  setIsAuth(isauth: boolean) {
    return this.eventSubHttpService.setIsAuth(isauth);
  }

  checkSubscription() {
    return this.eventSubHttpService.checkSubscription();
  }

  getCounters(params: SearchDashboardAPIParams) {
    return this.dashboardHttpService.fetchStats(params);
  }

  getStatistics(params: {
    brands: string[];
    searchDate: { start: string; end: string };
  }) {
    return this.dashboardHttpService.fetchStatistics(params);
  }

  getNumberConnections(params: SearchDate) {
    return this.dashboardHttpService.fetchNumberConnections(params);
  }

  getExport(params: SearchDashboardAPIParams) {
    return this.dashboardHttpService.exportData(params);
  }

  getFitterBrands() {
    return this.dashboardHttpService.fetchBrands();
  }

  getOktaAccount(
    encryptedInfo: string
  ): Observable<{ oktaAccount: OktaAccount }> {
    return this.authHttpService.getOktaAccount(encryptedInfo);
  }

  saveInsurerCriterias(criterias: Criteria[]): Observable<Criteria[]> {
    return this.administrationHttpService.saveCriterias(criterias);
  }

  getInsurerCriterias(): Observable<Criteria[]> {
    return this.administrationHttpService.listCriterias();
  }

  uploadDocuments(documents: Document[]): Observable<Document[]> {
    return this.storageHttpService.uploadDocuments(documents);
  }

  getSuperAdminFeatures(): Observable<any> {
    return this.storageHttpService.getFeaturesFiles();
  }

  @Dispatch()
  initFileUploadStatus(
    uploadType: UploadType
  ): Observable<UploadPartnerStatusInitAction> {
    // When we add new upload types, select actions here depending on the given upload type
    return of(new UploadPartnerStatusInitAction());
  }

  @Dispatch()
  updateFileUploadStatus(
    uploadStatus: UploadStatus
  ): Observable<UploadPartnerStatusUpdateAction> {
    return of(new UploadPartnerStatusUpdateAction(uploadStatus));
  }

  getFileUploadStatus(uploadType: UploadType): Observable<UploadStatus> {
    return this.fileHttpService.getFileUploadStatus(uploadType).pipe(
      map((uploadStatus) => {
        this.updateFileUploadStatus(uploadStatus);
        return uploadStatus;
      })
    );
  }

  longPoolingFileUploadStatus(
    uploadType: UploadType
  ): Observable<UploadStatus> {
    return interval(5000).pipe(
      switchMap(() => this.getFileUploadStatus(uploadType)),
      retry(),
      share(),
      first((state: UploadStatus) => {
        return !isUploadStatusRunning(state);
      })
    );
  }

  @Dispatch()
  setCurrentStep(
    step: number,
    disabled: boolean = false
  ): Observable<SetCurrentStepAction> {
    return of(new SetCurrentStepAction({ step, disabled }));
  }

  public canDoRequestAppointment(fitterBrand: string) {
    // Only allow the right environment to send request appointment with the right feature flips
    return (
      (this.featureFlippingService.isBrandFeatureActivated(
        fitterBrand,
        'b2b-allow-appointment-request'
      ) &&
        this.clientAppId === 'insurer') ||
      (this.featureFlippingService.isBrandFeatureActivated(
        fitterBrand,
        'b2c-allow-appointment-request'
      ) &&
        this.clientAppId === 'customer')
    );
  }

  getSmsStats(smsParams: GetSuperAdminSMSStatsAPI) {
    return this.smsHttpService.getSuperAdminSmsStats(smsParams);
  }

  downloadSmsStatsAsExcel(smsParams: GetSuperAdminSMSStatsAPI) {
    this.getSmsStats(smsParams)
      .pipe(take(1))
      .subscribe((smsStatsResponse) => {
        const sheetRows: SmsStatsSheetRow[] = [];

        const statsFitterRow: Record<
          string,
          Omit<Required<SmsStatsSheetRow>, 'fitterId' | 'total'>
        > = {};
        let nbFitterSmsTotal = 0;
        let nbCustomerSmsTotal = 0;
        smsStatsResponse.sms.forEach((smsStatsItem) => {
          if (!statsFitterRow[smsStatsItem.fitterId]) {
            statsFitterRow[smsStatsItem.fitterId] = {
              nbCustomerSms: 0,
              nbFitterSms: 0,
            };
          }

          if (smsStatsItem.smsType.includes('FITTER')) {
            statsFitterRow[smsStatsItem.fitterId].nbFitterSms++;
            nbFitterSmsTotal++;
          } else if (smsStatsItem.smsType.includes('CUSTOMER')) {
            statsFitterRow[smsStatsItem.fitterId].nbCustomerSms++;
            nbCustomerSmsTotal++;
          }
        });

        sheetRows.push(
          {
            fitterId: this.translator.getInstantTranslation(
              'Dashboard.smsStats.totalLineName'
            ),
            nbCustomerSms: nbCustomerSmsTotal,
            nbFitterSms: nbFitterSmsTotal,
            total: nbFitterSmsTotal + nbCustomerSmsTotal,
          },
          ...Object.keys(statsFitterRow).map(
            (fitterId): SmsStatsSheetRow => ({
              fitterId,
              nbCustomerSms: statsFitterRow[fitterId].nbCustomerSms,
              nbFitterSms: statsFitterRow[fitterId].nbFitterSms,
              total:
                statsFitterRow[fitterId].nbCustomerSms +
                statsFitterRow[fitterId].nbFitterSms,
            })
          )
        );

        this.excelFileExporterService.initNewWorkbook();

        this.excelFileExporterService.addSmsStatsPage(
          this.translator.getInstantTranslation(
            'Dashboard.smsStats.mainPageName'
          ),
          sheetRows
        );

        this.excelFileExporterService.exportFile(
          this.translator.getInstantTranslation('Dashboard.smsStats.fileName', {
            configId: this.configService.getConfig().id,
            dateTimeStart: dayjs(smsParams.start).format('DD-MM-YYYY'),
            dateTimeEnd: dayjs(smsParams.end).format('DD-MM-YYYY'),
          })
        );
      });
  }

  unsubscribe() {
    this.eventSubHttpService.unsubscribe();
  }

  formatSlotElementId(appointment: Appointment) {
    const apmtTime = dayjs(appointment?.time);
    return `slot-${appointment?.fitterId}-${
      appointment?.outsideIntervention ? 'out' : 'in'
    }-${apmtTime.isValid() ? apmtTime.format() : appointment?.id}`;
  }

  getSuperAdminAllConfig(): Observable<Configuration[]> {
    return this.configHttp.superAdminGetAllConfigs();
  }

  getSuperAdminStats(
    params: SuperAdminStatisticsAPIParams
  ): Observable<SuperAdminStatisticsApiResponse> {
    return this.dashboardHttpService.fetchSuperAdminStats(params);
  }

  getSuperAdminCosmosExports(
    params: SuperAdminStatisticsAPIParams
  ): Observable<SuperAdminCosmosExportApiResponse> {
    return this.dashboardHttpService.fetchSuperAdminCosmosData(params);
  }

  getSuperAdminSmsStats(params: SuperAdminSmsStatisticsAPIParams) {
    return this.dashboardHttpService.fetchSuperAdminSmsStats(params);
  }

  getICList() {
    return this.icListttpService.getICList();
  }
}
